import {
  formatDate,
  getDateString,
  getShiftedDate,
  today,
} from "../../../utils/datetime";

/**
 * Folded boxes and quiet detail lines for the work scope catalog.
 */

/**
 * Pick the scope with the highest count, breaking ties by title.
 *
 * Scopes with a zero count are skipped so an unused catalog shows a blank
 * leader instead of an arbitrary name.
 *
 * @param {Array<Object>} rows - Saved work scope rows.
 * @param {string} countKey - Numeric field to rank by.
 * @returns {Object|null}
 */
function mostUsedScope(rows, countKey) {
  return (
    rows
      .filter((row) => Number(row[countKey]) > 0)
      .sort((left, right) => {
        const byUse = (Number(right[countKey]) || 0) - (Number(left[countKey]) || 0);
        if (byUse !== 0) return byUse;
        return String(left.title || "").localeCompare(String(right.title || ""));
      })[0] || null
  );
}

/**
 * Pick the scope with the lowest positive count, breaking ties by title.
 *
 * @param {Array<Object>} rows - Saved work scope rows.
 * @param {string} countKey - Numeric field to rank by.
 * @returns {Object|null}
 */
function leastUsedScope(rows, countKey) {
  return (
    rows
      .filter((row) => Number(row[countKey]) > 0)
      .sort((left, right) => {
        const byUse = (Number(left[countKey]) || 0) - (Number(right[countKey]) || 0);
        if (byUse !== 0) return byUse;
        return String(left.title || "").localeCompare(String(right.title || ""));
      })[0] || null
  );
}

/**
 * Rounded share of a total, or an em dash when the total is zero.
 *
 * @param {number} part - Numerator.
 * @param {number} whole - Denominator.
 * @returns {string}
 */
function shareOf(part, whole) {
  if (!whole) return "—";
  return `${Math.round((Number(part) / whole) * 100)}%`;
}

/**
 * Mean of a number list, or an em dash when the list is empty.
 *
 * @param {Array<number>} values - Numbers to average.
 * @returns {string}
 */
function averageOf(values) {
  if (!values.length) return "—";
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  return Number.isInteger(mean) ? String(mean) : mean.toFixed(1);
}

/**
 * Median of a number list, or an em dash when the list is empty.
 *
 * @param {Array<number>} values - Numbers to rank.
 * @returns {string}
 */
function medianOf(values) {
  if (!values.length) return "—";
  const sorted = [...values].sort((left, right) => left - right);
  const mid = Math.floor(sorted.length / 2);
  const value =
    sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/**
 * How many scopes, starting from the busiest, cover at least half of the total count.
 *
 * @param {Array<Object>} rows - Saved work scope rows.
 * @param {number} totalUses - Sum of each scope's count.
 * @returns {number|string}
 */
function scopesCoveringHalf(rows, totalUses) {
  if (!totalUses) return "—";
  const ranked = [...rows].sort(
    (left, right) =>
      (Number(right.planning_usage_count) || 0) -
      (Number(left.planning_usage_count) || 0)
  );
  let running = 0;
  let count = 0;
  for (const row of ranked) {
    running += Number(row.planning_usage_count) || 0;
    count += 1;
    if (running * 2 >= totalUses) return count;
  }
  return count;
}

/**
 * Collapsed box for a named leader. The full title and count are the tooltip.
 *
 * @param {string} key - Stable metric key.
 * @param {string} label - Box label.
 * @param {Object|null} row - Winning scope, if any.
 * @param {string} countKey - Count field on that scope.
 * @returns {Object}
 */
function leaderMetric(key, label, row, countKey) {
  const title = row?.title || "";
  const count = row ? Number(row[countKey]) || 0 : 0;
  return {
    key,
    label,
    value: (
      <span className="block w-full truncate leading-7">{title || "—"}</span>
    ),
    title: title ? `${title} · ${count}` : undefined,
    valueClassName: "!text-sm font-semibold text-gray-950",
  };
}

/**
 * Quiet detail line. A title is set when the value is a scope name.
 *
 * @param {string} key - Stable line key.
 * @param {string} label - Left-hand label.
 * @param {string|number} value - Right-hand value.
 * @param {string} [title] - Tooltip for a truncated name.
 * @returns {Object}
 */
function detail(key, label, value, title) {
  return { key, label, value, title };
}

/**
 * One expanded column: a plain title and its detail lines.
 *
 * @param {string} key - Stable column key.
 * @param {string} title - Column heading.
 * @param {Array<Object>} items - Detail lines in this column.
 * @returns {{key: string, title: string, items: Array<Object>}}
 */
function category(key, title, items) {
  return { key, title, items };
}

/**
 * Build the metric boxes and the quieter expanded lines.
 *
 * Counts come from the loaded list. Each count is the distinct dispatch
 * dates already annotated on that row, excluding skipped days, including the
 * last-30-day count.
 *
 * @param {Array<Object>} rows - Saved work scope rows.
 * @returns {{ folded: Array<Object>, expandedDetails: Array<{key: string, title: string, items: Array<Object>}> }}
 */
export function buildWorkScopeStats(rows) {
  const list = Array.isArray(rows) ? rows.filter((row) => !row._draft) : [];
  const active = list.filter((row) => row.active);
  const inactive = list.filter((row) => !row.active);
  const uses = (row) => Number(row.planning_usage_count) || 0;
  const uses30 = (row) => Number(row.planning_usage_count_30d) || 0;
  const totalUses = list.reduce((sum, row) => sum + uses(row), 0);
  const totalUses30 = list.reduce((sum, row) => sum + uses30(row), 0);
  const activeUses = active.map(uses);
  const allTimeLeader = mostUsedScope(list, "planning_usage_count");
  const recentLeader = mostUsedScope(list, "planning_usage_count_30d");
  const leastActive = leastUsedScope(active, "planning_usage_count");
  const cutoff = getDateString(getShiftedDate(today(), -30));
  const createdRecently = list.filter(
    (row) => String(row.created || "").slice(0, 10) >= cutoff
  );
  const newest = [...list]
    .filter((row) => row.created)
    .sort((left, right) => String(right.created).localeCompare(String(left.created)))[0];
  const newestLabel = newest?.created
    ? formatDate(String(newest.created).slice(0, 10), "month DD, YYYY")
    : "";
  const unitNames = new Set(list.map((row) => row.unit?.name).filter(Boolean));

  return {
    folded: [
      { key: "active", label: "Active", value: active.length },
      { key: "inactive", label: "Inactive", value: inactive.length },
      leaderMetric("most-used", "Most Used", allTimeLeader, "planning_usage_count"),
      leaderMetric(
        "most-used-30",
        "Most Used (30 days)",
        recentLeader,
        "planning_usage_count_30d"
      ),
    ],
    expandedDetails: [
      category("catalog", "Catalog", [
        detail("total", "Scopes", list.length),
        detail("missing-unit", "Missing a unit", list.filter((row) => !row.unit).length),
        detail(
          "active-missing-unit",
          "Active missing a unit",
          active.filter((row) => !row.unit).length
        ),
        detail("units", "Units in use", unitNames.size),
        detail("created-30", "Created in 30 days", createdRecently.length),
        detail(
          "newest",
          newestLabel ? `Newest (${newestLabel})` : "Newest",
          newest?.title || "—",
          newest?.title
        ),
      ]),
      category("uses", "Count", [
        detail("uses", "Count", totalUses),
        detail("uses-30", "Count (30 days)", totalUses30),
        detail("avg-active", "Average count (active)", averageOf(activeUses)),
        detail("median-active", "Median count (active)", medianOf(activeUses)),
        detail(
          "least",
          "Least-used active",
          leastActive?.title || "—",
          leastActive ? `${leastActive.title} · ${uses(leastActive)}` : undefined
        ),
      ]),
      category("spread", "Spread", [
        detail(
          "leader-share",
          "Most-used share",
          shareOf(allTimeLeader ? uses(allTimeLeader) : 0, totalUses)
        ),
        detail(
          "leader-share-30",
          "Most-used share (30 days)",
          shareOf(recentLeader ? uses30(recentLeader) : 0, totalUses30)
        ),
        detail("half", "Scopes covering half the count", scopesCoveringHalf(list, totalUses)),
      ]),
      category("idle", "Idle", [
        detail(
          "unused",
          "Active with a count of 0",
          active.filter((row) => !uses(row)).length
        ),
        detail(
          "idle-30",
          "Active with a count of 0 in 30 days",
          active.filter((row) => !uses30(row)).length
        ),
        detail(
          "inactive-used",
          "Inactive still referenced",
          inactive.filter((row) => uses(row) > 0).length
        ),
        detail(
          "inactive-recent",
          "Inactive used in 30 days",
          inactive.filter((row) => uses30(row) > 0).length
        ),
      ]),
    ],
  };
}
